/**
 * 인계 유형별 링크 슬롯 (Resources 핸드오프 §4).
 *   PD (PM → Designer)        prd   PRD
 *   DD (Designer → Developer)  figma Figma file · ds Design system page
 *   QA (Developer → PM)        web   Working web link · repo GitHub repository · readme README with open-source list
 *   FINAL (최종 결과물)          prdf  PRD final · qa QA report · demo Demo (optional)
 *   그 외(마케터 · 리더 → 팀장 일반)는 슬롯 없이 자유 링크.
 * 제출하면 deliverable.slots = [{ k, l, url }] (빈 슬롯 제외) · links = 슬롯 주소들.
 */

/**
 * [key, label, rule, flag] — rule: doc · figma · github · https,
 * flag: optional · repo(공개 저장소 안내) · live(배포 링크 유지 안내)
 */
export const HO_SLOTS = {
  PD: [['prd', 'PRD', 'doc']],
  DD: [
    ['figma', 'Figma file', 'figma'],
    ['ds', 'Design system page', 'figma'],
  ],
  QA: [
    ['web', 'Working web link', 'https', 'live'],
    ['repo', 'GitHub repository', 'github', 'repo'],
    ['readme', 'README with open-source list', 'github'],
  ],
  FINAL: [
    ['prdf', 'PRD final', 'doc'],
    ['qa', 'QA report', 'doc'],
    ['demo', 'Demo', 'doc', 'optional'],
  ],
};

/** 꼭 채워야 하는 칸 — QA 는 배포 링크 · GitHub 저장소 (서버 TdTaskList.REQUIRED_QA_SLOTS 와 같다) */
export const REQUIRED_SLOTS = { QA: ['web', 'repo'] };

/** 비어 있는 필수 칸의 이름 */
export const missingRequired = (ho, slots, rows) =>
  (REQUIRED_SLOTS[ho] ?? [])
    .filter((k) => !rows.some((r) => r.tag === k && r.url.trim()))
    .map((k) => slots.find(([sk]) => sk === k)?.[1] ?? k);

export const TAG_FIELD = {
  prd: 'prd_url',
  figma: 'figma_url',
  ds: 'design_system_url',
  web: 'web_url',
  repo: 'repo_url',
  readme: 'oss_libraries',
  prdf: 'prd_url',
  qa: 'qa_report_url',
  demo: 'demo_url',
};

export const SLOT_PLACEHOLDER = {
  figma: 'https://www.figma.com/file/…',
  github: 'https://github.com/…',
  https: 'https://…',
  doc: 'https://…',
};

/** 인계 유형 — 최종 결과물이면 FINAL, 아니면 담당 직무 → 받는 직무 */
export const hoOf = (task, assigneeJob, receiverJob) => {
  if (task?.isFinal) return 'FINAL';
  // 서버가 정해 둔 인계 유형 — 담당자를 다른 직무로 바꿔도 같은 칸 (MK 는 자유 링크)
  if (task?.handoffType !== undefined) {
    return ['PD', 'DD', 'QA'].includes(task.handoffType) ? task.handoffType : null;
  }
  if (assigneeJob === 'planner' && receiverJob === 'designer') return 'PD';
  if (assigneeJob === 'designer' && receiverJob === 'developer') return 'DD';
  if (assigneeJob === 'developer' && receiverJob === 'planner') return 'QA';
  return null;
};

const hostOf = (url) =>
  (url ?? '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .split(/[/?#]/)[0]
    .replace(/:\d+$/, '')
    .replace(/^www\./, '')
    .toLowerCase();

export const LINK_KINDS = ['Figma', 'GitHub', 'Notion', 'Web'];
export const kindRule = (kind) =>
  kind === 'Figma' ? 'figma' : kind === 'GitHub' ? 'github' : 'doc';

export const DRIVE_MSG =
  'Google Drive links can’t be used. Share it as a Google Docs, Notion, or Figma link instead.';
export const isDriveUrl = (raw) => /(^|\.)drive\.google\.com$/.test(hostOf(raw));

/** http(s) 전체 주소 — 도메인(점이 있는 호스트), localhost, IP. 포트·경로는 있어도 된다 */
export const isWebUrl = (raw) => {
  const url = (raw ?? '').trim();
  if (!/^https?:\/\/\S+$/i.test(url)) return false;
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || /\.[a-z0-9-]+$/i.test(hostname);
  } catch {
    return false;
  }
};

/** 입력 즉시 검사 — 맞으면 null, 아니면 오류 문구 */
export const slotCheck = (rule, raw) => {
  const url = (raw ?? '').trim();
  if (!url) return null;
  const host = hostOf(url);
  if (isDriveUrl(url)) return DRIVE_MSG;
  if (!isWebUrl(url)) return 'Paste a full link.';
  if (rule === 'figma' && !/(^|\.)figma\.com$/.test(host)) return 'Use a figma.com link.';
  if (rule === 'github' && !/(^|\.)github\.com$/.test(host)) return 'Use a github.com link.';
  if (rule === 'https' && !/^https:\/\//i.test(url)) return 'Use a link that starts with https://.';
  return null;
};

/** (i) 툴팁 3줄 — 허용 · Drive 불가 · 대안 */
export const SLOT_TIPS = {
  doc: [
    'Accepts: Google Docs, Notion, Figma or any public https:// link.',
    'Google Drive links aren’t accepted.',
    'Instead: share it as a Google Docs, Notion, or Figma link.',
  ],
  figma: [
    'Accepts: figma.com file or prototype links.',
    'Google Drive links aren’t accepted.',
    'Instead: share the Figma file so anyone with the link can view, then paste it here.',
  ],
  github: [
    'Accepts: github.com links only. The repository must be public, with a README at the root.',
    'Google Drive links aren’t accepted.',
    'Instead: push it to a public GitHub repository and paste that link.',
  ],
  https: [
    'Accepts: a live https:// link where anyone can use the core flow.',
    'Google Drive links aren’t accepted.',
    'Instead: deploy the build (for example on Vercel or Netlify) and paste its live link.',
  ],
};

/** 최종 결과물 — 한 장 요약 4문항 (각 1,500자) */
export const ONE_PAGE = [
  { q: 'What problem did you pick and why?' },
  { q: 'How did you solve it?' },
  { q: 'What did you leave out and why?', hint: 'Include how you handled change notices.' },
  { q: 'Where did you use AI?' },
];
export const ONE_PAGE_MAX = 1500;

/** 최종 결과물 모달의 승인된 팀 링크 (읽기 전용) — 보류 · 백로그. 로직은 재개 시 쓰려고 남겨 둔다 */
export const APPROVED_LINKS = [
  ['web', 'Working web link', 'Web', 'developer'],
  ['figma', 'Figma file', 'Figma', 'designer'],
  ['ds', 'Design system page', null, 'designer'],
  ['repo', 'GitHub repository', 'GitHub', 'developer'],
];

/**
 * 승인된 태스크 버전에서 슬롯 값을 찾는다. 슬롯이 없던 예전 제출이면 그 직무의 승인 링크 중 종류가 맞는 것.
 * @returns [{ k, l, url|null }]
 */
export const approvedLinks = (tasks, memberById, assigneesOf) => {
  const approved = tasks.filter((t) => t.state === 'approved' && t.deliverable);
  return APPROVED_LINKS.map(([k, l, kind, job]) => {
    for (const t of approved) {
      const hit = (t.deliverable.slots ?? []).find((s) => s.k === k && s.url);
      if (hit) return { k, l, url: hit.url };
    }
    if (kind) {
      for (const t of approved) {
        if (!assigneesOf(t).some((id) => memberById.get(id)?.job === job)) continue;
        const kinds = t.deliverable.linkKinds ?? [];
        const url = (t.deliverable.links ?? []).find((u, j) => kinds[j] === kind);
        if (url) return { k, l, url };
      }
    }
    return { k, l, url: null };
  });
};

/** 버전 카드·Lead inbox 줄 — 슬롯이 있으면 "{label} · {주소}", 없으면 예전 links */
export const deliverableLinkRows = (d) => {
  if (d?.slots?.length)
    return d.slots.filter((s) => s.url).map((s) => ({ label: s.l, url: s.url }));
  const links = d?.links?.length ? d.links : d?.link ? [d.link] : [];
  return links.map((url) => ({ label: null, url }));
};

export const stripScheme = (url) => (url ?? '').replace(/^https?:\/\/(www\.)?/, '');
export const linkRowText = (r) => (r.label ? `${r.label} · ${stripScheme(r.url)}` : r.url);
